import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';

import { ConversationThread } from './claude/conversation_thread';
import { type Message } from './claude/message';
import './ConversationView.css';

interface ConversationViewProps {
  conversationThread: ConversationThread;
  className?: string;
  onClose?: () => void;
  onFocus?: () => void;
  openConversationThread: (parent: ConversationThread, child: ConversationThread) => void;
  showCloseButton?: boolean;
}

function ConversationView({
  conversationThread,
  className,
  onClose,
  onFocus,
  openConversationThread,
  showCloseButton,
}: ConversationViewProps) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingBuffer, setStreamingBuffer] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const items = conversationThread.getAllItems(); // Now includes both messages and child threads

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    conversationThread.registerOnToken((token: string) => {
      // If we're receiving tokens, it probably means that a user query is kicked off
      // as part of the topic selection process.
      setIsLoading(true);

      // Update streaming buffer with new token
      setStreamingBuffer(prev => prev + token);
    });

    conversationThread.registerOnComplete((_: Message) => {
      // Clear streaming buffer and show final message
      setStreamingBuffer('');
      setIsLoading(false);
    });

    conversationThread.registerOnError((error: Error) => {
      setStreamingBuffer('');
      setIsLoading(false);

      toast.error(`Error: ${error.message}`);

      // If there's an error, retry the user request after 5 seconds.
      setTimeout(() => conversationThread.retryUserQuery(), 5000);
    });

    return () => {
      conversationThread.unregisterOnToken();
      conversationThread.unregisterOnComplete();
      conversationThread.unregisterOnError();
    };
  }, [conversationThread]);

  useEffect(() => {
    scrollToBottom();
  }, [items.length]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userInput = input.trim();
    setInput('');
    setIsLoading(true);

    conversationThread.sendUserQuery(userInput);

    // Clear any existing streaming buffer
    setStreamingBuffer('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderItem = (item: Message | ConversationThread, index: number) => {
    // Check if item is a ConversationThread
    if (item instanceof ConversationThread) {
      // Render child thread banner
      const messagesCount = item.getMessageOnlyCount();
      return (
        <div key={`thread-${index}`} className="thread-banner">
          <div className="thread-banner-content">
            <div className="thread-info">
              <span className="thread-title">
                {item.getInitialUserQuery() || 'Conversation Branch'}
              </span>
              <span className="thread-count">
                {messagesCount} message{messagesCount !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              className="thread-open-button"
              onClick={() => openConversationThread(conversationThread, item)}
            >
              Open
            </button>
          </div>
        </div>
      );
    } else {
      // Render message
      return (
        <div key={item.id} className={`message ${item.role}`}>
          <div className="message-content">
            <ReactMarkdown>{item.content}</ReactMarkdown>
          </div>
        </div>
      );
    }
  };

  return (
    <div className={`chat-window ${items.length ? 'started' : 'empty'} ${className || ''}`} onFocus={onFocus}>
      <div className="chat-container">
        <header className="chat-header">
          <h1>{conversationThread.getMessageOnlyCount() ? conversationThread.getInitialUserQuery() : 'Spark'}</h1>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="close-button"
              title="Close conversation"
            >
              ×
            </button>
          )}
        </header>

        <div className="messages-container">
          {items.map((item, index) => renderItem(item, index))}
          {isLoading && (
            <div className="message assistant streaming">
              <div className="message-content">
                <ReactMarkdown>{streamingBuffer}</ReactMarkdown>
                <span className="streaming-cursor">|</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <div className="message-input-row">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={conversationThread.getMessageOnlyCount() ? "" : "What would you like to learn about?"}
              disabled={isLoading}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationView;